type BuilderBlock = {
  id?: string;
  tagName?: string;
  linkUrl?: string;
  properties?: Record<string, unknown>;
  children?: BuilderBlock[];
  component?: {
    name?: string;
    options?: {
      columns?: { blocks?: BuilderBlock[]; [key: string]: unknown }[];
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type BuilderContent = {
  data?: { blocks?: BuilderBlock[]; [key: string]: unknown };
  [key: string]: unknown;
};

function applyLinkPatch(block: BuilderBlock, blockId: string, href: string): BuilderBlock {
  if (block.id !== blockId) return block;
  return {
    ...block,
    tagName: 'a',
    linkUrl: href,
    properties: { ...block.properties, href },
  };
}

function mapBlockTree(
  block: BuilderBlock,
  map: (block: BuilderBlock) => BuilderBlock
): BuilderBlock {
  let next = map(block);

  if (next.children?.length) {
    next = { ...next, children: next.children.map(child => mapBlockTree(child, map)) };
  }

  const columns = next.component?.options?.columns;
  if (columns?.length) {
    next = {
      ...next,
      component: {
        ...next.component,
        options: {
          ...next.component?.options,
          columns: columns.map(column => ({
            ...column,
            blocks: column.blocks?.map(child => mapBlockTree(child, map)) ?? column.blocks,
          })),
        },
      },
    };
  }

  return next;
}

export function stripHiddenBlocks(
  content: BuilderContent,
  hiddenBlockIds: readonly string[]
): BuilderContent {
  if (!content.data?.blocks?.length || hiddenBlockIds.length === 0) return content;

  const hidden = new Set(hiddenBlockIds);
  const filterBlock = (block: BuilderBlock): BuilderBlock | null => {
    if (block.id && hidden.has(block.id)) return null;

    let next = block;
    if (block.children?.length) {
      const children = block.children
        .map(child => filterBlock(child))
        .filter((child): child is BuilderBlock => child !== null);
      next = { ...next, children };
    }

    const columns = block.component?.options?.columns;
    if (columns?.length) {
      next = {
        ...next,
        component: {
          ...block.component,
          options: {
            ...block.component?.options,
            columns: columns.map(column => ({
              ...column,
              blocks:
                column.blocks
                  ?.map(child => filterBlock(child))
                  .filter((child): child is BuilderBlock => child !== null) ?? column.blocks,
            })),
          },
        },
      };
    }

    return next;
  };

  const filterBlocks = (blocks: BuilderBlock[]): BuilderBlock[] =>
    blocks
      .map(block => filterBlock(block))
      .filter((block): block is BuilderBlock => block !== null);

  return {
    ...content,
    data: { ...content.data, blocks: filterBlocks(content.data.blocks) },
  };
}

export function patchBlockLinks(
  content: BuilderContent,
  blockIds: readonly string[],
  href: string
): BuilderContent {
  if (!content.data?.blocks?.length || blockIds.length === 0) return content;

  const patchIds = new Set(blockIds);
  const patchBlocks = (blocks: BuilderBlock[]): BuilderBlock[] =>
    blocks.map(block =>
      mapBlockTree(block, current =>
        current.id && patchIds.has(current.id) ? applyLinkPatch(current, current.id, href) : current
      )
    );

  return {
    ...content,
    data: { ...content.data, blocks: patchBlocks(content.data.blocks) },
  };
}
